/**
 * Shared "hide/show financials" toggle.
 *
 * Persisted in localStorage. Cross-component sync uses a module-level
 * subscriber set rather than DOM events to avoid double-render races.
 */
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "dd.financialsHidden";

function readInitial() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// Module-level state shared by every hook instance.
let _hidden = readInitial();
const _subscribers = new Set();

function _set(next) {
  if (_hidden === next) return;
  _hidden = next;
  try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  for (const fn of _subscribers) fn(next);
}

export function useFinancialsVisible() {
  const [hidden, setHidden] = useState(_hidden);

  useEffect(() => {
    // Subscribe to module-level changes
    _subscribers.add(setHidden);
    // Resync in case the module value changed before this component mounted
    setHidden(_hidden);

    // Cross-tab sync via storage events
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) _set(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      _subscribers.delete(setHidden);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const toggle = useCallback(() => {
    _set(!_hidden);
  }, []);

  return { hidden, toggle };
}
